import { useEffect, useState } from 'react'
import {
  disablePush,
  enablePush,
  explainHealth,
  explainStatus,
  pushHealth,
  pushStatus,
  sendTestNotification,
  type PushHealth,
  type PushStatus,
} from '@core/push'
import { Button, Card, ErrorNote } from '@design/components'

/**
 * The notifications toggle.
 *
 * Deliberately a button and not a switch: on iOS, asking for permission is a
 * one-shot, irreversible action. If the person declines, the app can never ask
 * again — only iOS Settings can undo it. A switch implies you can flip it
 * back, so a switch would be lying.
 */
export function PushSetting({ userId }: { userId: string }) {
  const [status, setStatus] = useState<PushStatus>(() => pushStatus())
  const [busy, setBusy] = useState(false)
  const [health, setHealth] = useState<PushHealth | null>(null)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setStatus(pushStatus())
    pushHealth().then(setHealth).catch(() => setHealth(null))
  }, [])

  const canAsk = status === 'default'
  const on = status === 'granted'

  return (
    <Card className={`mb-4 ${on ? '' : 'border-warn/40 bg-warn/5'}`}>
      <p className="font-medium mb-1">Meldingen</p>
      <p className="text-sm text-muted mb-3">{explainStatus(status)}</p>

      {canAsk && (
        <Button
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            setError('')
            try {
              // Must run inside this click: iOS refuses a permission request
              // that is not tied to a user gesture.
              setStatus(await enablePush(userId))
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Aanzetten lukte niet.')
            } finally {
              // In a finally, because a throw here used to leave the button
              // saying "Bezig…" until the app was restarted.
              setBusy(false)
            }
          }}
        >
          {busy ? 'Bezig…' : 'Meldingen aanzetten'}
        </Button>
      )}

      {canAsk && <ErrorNote>{error}</ErrorNote>}

      {on && (
        <>
          {/* The single most load-bearing untested path in the app. Everything
              downstream of "your invoice is handled" depends on a notification
              actually arriving, and four separate things can fail silently on
              the way. This turns "I hope so" into a fact. */}
          <Button
            tone="secondary"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              setError('')
              setSent(false)
              try {
                await sendTestNotification()
                setSent(true)
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Versturen lukte niet.')
              }
              setBusy(false)
            }}
          >
            {busy ? 'Bezig…' : 'Stuur mezelf een test'}
          </Button>

          {sent && (
            <p className="text-sm text-muted mt-2">
              Onderweg. Hij komt binnen een minuut aan — de worker kijkt elke
              minuut. Komt hij niet, dan ligt het niet aan jouw toestel maar aan
              de aflevering, en dat staat hieronder.
            </p>
          )}

          <ErrorNote>{error}</ErrorNote>

          {explainHealth(health) && (
            <p className="text-xs text-muted mt-2">{explainHealth(health)}</p>
          )}

          <button
            onClick={async () => {
              setBusy(true)
              setError('')
              try {
                await disablePush(userId)
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Uitzetten lukte niet.')
              } finally {
                setStatus(pushStatus())
                setBusy(false)
              }
            }}
            className="block mt-3 text-sm text-muted underline underline-offset-2"
          >
            Op dit toestel uitzetten
          </button>
        </>
      )}
    </Card>
  )
}
